import { useEffect, useRef, useState } from "react";

// Sprachwahl im Kopf (Version 3), aufgebaut wie die Einstellungen: ein quadratisches Feld mit einer Weltkugel.
// Beim Klick wächst das Feld nach links und unten auf, die Kugel dreht sich ein Stück, darunter stehen die Sprachen.

const T = {
    de: { title: "Sprache", open: "Sprache wählen", close: "Sprachwahl schließen" },
    en: { title: "Language", open: "Choose language", close: "Close language selection" },
};
const LANGS = [{ code: "de", label: "Deutsch" }, { code: "en", label: "English" }];

// Weltkugel genau nach Variante A (Wunsch 21.10.2026): Linienzeichnung mit Umriss, Bogen und Mittellinie,
// ohne waagerechte Linien. Die linke Hälfte ist dunkel, die rechte Hälfte orange.
// Beim Klick rollen die Längengrade ein Stück weiter, die Kugel dreht sich sichtbar um ihre Achse.
const CX = 12, CY = 12, R = 9;
const STEP = 16; // ein Längengrad-Paar (Mittellinie und Bogen); pro Klick rollt die Kugel um ein Paar weiter
const MERIDIANS = Array.from({ length: 26 }, (_, i) => -STEP * (i - 1));
const Lines = ({ turn, uid }) => (
    <>
        <circle cx={CX} cy={CY} r={R} className="ls-line" />
        <g clipPath={`url(#ls-clip-${uid})`}>
            <g className="ls-meridians" style={{ transform: `translateX(${(turn % 24) * STEP}px)` }}>
                {MERIDIANS.map((x) => (
                    <g key={x}>
                        <ellipse cx={CX + x} cy={CY} rx="4" ry={R} className="ls-line" />
                        <path d={`M${CX + x} ${CY - R}V${CY + R}`} className="ls-line" />
                    </g>
                ))}
            </g>
        </g>
    </>
);
const Globe = ({ turn, uid }) => (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" className="ls-globe">
        <defs>
            <clipPath id={`ls-clip-${uid}`}><circle cx={CX} cy={CY} r={R} /></clipPath>
            <clipPath id={`ls-left-${uid}`}><rect x="0" y="0" width={CX} height="24" /></clipPath>
            <clipPath id={`ls-right-${uid}`}><rect x={CX} y="0" width="12" height="24" /></clipPath>
        </defs>
        <g clipPath={`url(#ls-left-${uid})`} className="ls-dark"><Lines turn={turn} uid={uid} /></g>
        <g clipPath={`url(#ls-right-${uid})`} className="ls-orange"><Lines turn={turn} uid={uid} /></g>
    </svg>
);

let n = 0;

const LanguageSettings = ({ lang, onSelect, mark }) => {
    const L = T[lang === "en" ? "en" : "de"];
    const [open, setOpen] = useState(false);
    const [turn, setTurn] = useState(0);
    const [uid] = useState(() => (n += 1));
    const ref = useRef(null);

    const toggle = () => { setOpen((o) => !o); setTurn((t) => t + 1); };
    // Öffnet nur per Klick oder Tastatur, nicht beim Überfahren mit der Maus

    useEffect(() => {
        if (!open) return undefined;
        const close = () => { setOpen(false); setTurn((t) => t + 1); };
        const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
        const onKey = (e) => { if (e.key === "Escape") close(); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
    }, [open]);

    return (
        <div className={`vs-wrap ls-wrap ${open ? "is-open" : ""}`} ref={ref} data-testid="language-settings">
            <div className="vs-surface ls-surface">
                <div className="vs-head">
                    <span className="vs-title" aria-hidden={!open}>{L.title}</span>
                    <button
                        type="button"
                        onClick={toggle}
                        aria-expanded={open}
                        aria-controls={`ls-body-${uid}`}
                        aria-label={open ? L.close : `${L.open} (${lang.toUpperCase()})`}
                        title={L.title}
                        data-testid="language-settings-trigger"
                        className="vs-trigger ls-trigger"
                    >
                        {mark === "code" ? <span className="ls-code">{(lang || "de").toUpperCase()}</span> : <Globe turn={turn} uid={uid} />}
                    </button>
                </div>
                <div id={`ls-body-${uid}`} className="vs-body" inert={open ? undefined : true}>
                    <div className="vs-body-inner" role="listbox" aria-label={L.title}>
                        {LANGS.map((o) => (
                            <button
                                key={o.code}
                                type="button"
                                role="option"
                                aria-selected={lang === o.code}
                                data-testid={`language-settings-${o.code}`}
                                onClick={() => { setOpen(false); setTurn((t) => t + 1); if (o.code !== lang) onSelect(o.code); }}
                                className={`vs-row ls-option ${lang === o.code ? "is-on" : ""}`}
                            >
                                <span>{o.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LanguageSettings;
